import {
  Section,
  Hero,
  Content,
  Heading,
  Container,
  Columns,
  Image,
  Media,
  Button,
  Box,
  Message
} from "react-bulma-components";
import { currencies } from "country-data";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  COLORS,
  RECAPTCHA_KEY,
  NEWSLETTER_FREQ_CHOICES,
  DAILY_DIGEST,
  FRONTEND
} from "../../constants";
import { displayCurrency } from "../../utils";
import { useState, Fragment } from "react";
import CalculatorForm from "../../components/calculators/EarningsCalculatorForm";
import SubscribeForm, {
  submitSubscribe
} from "../../components/forms/NewsletterSubscriptionForm";
import Icon from "../../components/Icon";
import { NextSeo } from "next-seo";
import { firebase } from "../../lib/firebase";
import "firebase/auth";
import SignIn from "../../components/auth/SignIn";
import { useAuth } from "../../lib/use-auth";

const availCurrencyCodes = currencies.all.map(currency => currency.code);

// import ExpectedEarnings from "../../components/calculators/ExpectedEarnings";
const earningFormSchema = Yup.object().shape({
  email: Yup.string()
    .max(30, "Can't be longer than 30 characters")
    .email("Must be a valid email")
    .required(),
  fbFriends: Yup.number()
    .default(0)
    .min(0, "Invalid input."),
  linkedInFriends: Yup.number()
    .default(0)
    .min(0, "Invalid input."),
  duplication: Yup.number()
    .default(80)
    .min(0, "Invalid input.")
    .max(100, "Maximum allowed value is 100%"),
  numMentees: Yup.number()
    .default(0)
    .min(0, "Invalid input."),
  monthlySalary: Yup.number().default(500),
  monthlySalaryCurrency: Yup.string()
    .default("USD")
    .oneOf(availCurrencyCodes),
  frequency: Yup.string().oneOf(NEWSLETTER_FREQ_CHOICES),
  tags: Yup.array(
    Yup.string()
      .lowercase()
      .strip()
  ).max(10, "Select upto 10 filters"),
  terms: Yup.boolean().oneOf(
    [true],
    "Please confirm that you accept the Privacy Policy"
  ),
  news: Yup.boolean()
});

const DEFAULT_CLOSE_NETWORK_SIZE = 50;

const calculateEarnings = values => {
  let networkSize = 0;
  const twoNetworksPresent = values.fbFriends > 0 && values.linkedInFriends > 0;
  if (twoNetworksPresent) {
    let networkSizes = [values.linkedInFriends, values.fbFriends];
    networkSizes.sort((a, b) => a - b); // in-place sort
    const smallerCount = networkSizes[0];
    const largerCount = networkSizes[1];
    const overlapValue = Math.floor((values.duplication / 100) * smallerCount);
    networkSize = smallerCount + largerCount - overlapValue;
  } else {
    networkSize = values.fbFriends + values.linkedInFriends;
  }
  const closeNetwork = DEFAULT_CLOSE_NETWORK_SIZE;
  const yearlyCloseChange = closeNetwork / 4 + values.numMentees;
  const stars = networkSize >= 1000 ? Math.floor(networkSize * 0.01) : 0;
  const yearlyStarChange = stars / 4;
  // assuming your referral gets picked up about 1 in 10 times.
  // also, idiots don't want to believe they can earn that much.
  const refBonus = values.monthlySalary / 10;
  const yearlyCloseEarnings = Math.round(refBonus * yearlyCloseChange);
  const yearlyStarEarnings = Math.round(refBonus * yearlyStarChange);
  const yearlyCloseDisplay = displayCurrency(
    yearlyCloseEarnings,
    values.monthlySalaryCurrency
  );
  const includingRandomUpside = displayCurrency(
    yearlyCloseEarnings + yearlyStarEarnings,
    values.monthlySalaryCurrency
  );
  // const numDirectConnections =
  return {
    networkSize: networkSize,
    bottomLine: `${yearlyCloseDisplay}`,
    upside: `${includingRandomUpside}`
  };
};

const HowMuchCanEarn = () => {
  const [showResults, setShowResults] = useState(false);
  const [learnEst, setLearnEst] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();

  // pitch
  const pitch = (
    <Content>
      <br />
      We need a few details from you to make this estimate.
      <br />
      <br />
      <center>
        <Icon icon={"hand-point-down"} size="large" faSize="3x" />
      </center>
    </Content>
  );

  const methodology = (
    <Fragment>
      <small>
        <br />
        <br />
        <p>
          Companies on BountyHiring reward users who refer successful candidates
          to their jobs. Because people know their colleagues/friends better
          than LinkedIn or a Resumé. <br />
          <br />
          Your success depends on:
        </p>
        <ul>
          <li>
            Breadth
            <br />
            How wide and diverse your network is
          </li>
          <li>
            Quality
            <br />
            How accurately you map talent to the right job. The best way to
            successfully place a candidate is by making sure they fit the
            requirements.
          </li>
          <li>
            Speed
            <br />
            How fast you respond to job ads. We can help by notifying you when
            new jobs get posted.
          </li>
        </ul>
        We base this estimation on three statistical facts.
        <ul>
          <li>On average, people change jobs every 4 years.</li>
          <li>
            On average, people maintain a regular connection with 50 people.
          </li>
          <li>
            Companies spend about 10 - 30% of annual salary on acquiring one
            employee
          </li>
        </ul>
        <br />
        The base figure is derived from these facts. We also include a
        conservative estimate of the number of "Rocket Scientists" in your
        network.
      </small>
    </Fragment>
  );

  // on success, ask to confirm in their inbox

  // on confirm, show share buttons

  return (
    <Fragment>
      <NextSeo
        title={`Calculator to estimate yearly earnings on BountyHiring`}
        description={`Companies on BountyHiring reward users who refer
        successful candidates to their jobs. Calculate your network potential using this calculator`}
        openGraph={{
          type: "website",
          url: `${FRONTEND}/calculator/how-much-can-earn`,
          title: `Calculator to estimate yearly earnings on BountyHiring`,
          description: `Companies on BountyHiring reward users who refer
          successful candidates to their jobs. Calculate your network potential using this calculator`,
          images: [
            {
              url: `${FRONTEND}/images/calculator-featured.png`
            }
          ]
        }}
      />
      <Section>
        <Image src={`/images/calculator.png`} />
        <Hero size="small">
          <Hero.Body>
            {/* <Container>
              <Heading subtitle={false}>
                Calculator to estimate yearly earnings on BountyHiring
              </Heading>
              <Heading subtitle>
                Get a sense for how much you can earn with your current network
              </Heading> 
            </Container> */}
          </Hero.Body>
        </Hero>
        <Container>
          {/* <Columns>
            <Columns.Column size="half">{pitch}</Columns.Column>
            <Columns.Column size="half">
              
            </Columns.Column>
          </Columns> */}
          <Formik
            initialValues={{
              email: "",
              fbFriends: 0, // included in networkSize
              linkedInFriends: 0, // included in networkSize
              duplication: 80, // included in networkSize
              numMentees: 0,
              monthlySalary: 1000,
              monthlySalaryCurrency: "USD",
              frequency: DAILY_DIGEST,
              tags: [],
              terms: false,
              news: false
            }}
            validationSchema={earningFormSchema}
            validate={async values => {
              try {
                await earningFormSchema.validate(values);
              } catch (err) {
                // schema errors don't override by return errors
                return;
              }
            }}
            onSubmit={(values, { setErrors, setSubmitting }) => {
              const { networkSize } = calculateEarnings(values);
              const inputValues = {
                ...values,
                networkSize
              };
              delete inputValues.fbFriends;
              delete inputValues.linkedInFriends;
              delete inputValues.duplication;
              delete inputValues.terms;
              submitSubscribe({
                inputValues,
                setErrors,
                setSubmitting,
                setSuccess
              });
            }}
          >
            {props => {
              const { bottomLine, upside } = calculateEarnings(props.values);

              let estimate;
              if (bottomLine !== upside) {
                estimate = `${bottomLine} - ${upside}`;
              } else {
                estimate = `${bottomLine}`;
              }

              return (
                <Columns>
                  <Columns.Column size="one-third">
                    <CalculatorForm
                      {...props}
                      setShowResults={setShowResults}
                    />
                  </Columns.Column>
                  {showResults && (
                    <Columns.Column size="one-third" offset="one-fifth">
                      <Box>
                        <Content textAlignment="centered">
                          <Heading>You can earn</Heading>
                          Based on your inputs, we estimate that you can
                          potentially earn
                          <Heading>{estimate}*</Heading>
                          per year
                        </Content>
                        <Content>
                          <small>
                            *Disclaimer: this is{" "}
                            <u>
                              <b>not guaranteed</b>
                            </u>{" "}
                            income but a statistical estimation.{" "}
                            {/* <a
                              onClick={() => {
                                setLearnEst(!learnEst);
                              }}
                            >
                              {learnEst ? "Collapse" : "Learn More"}
                            </a> */}
                            .&nbsp;
                          </small>
                          {methodology}
                          <br />
                          <br />
                          {/* An important factor to decide your earnings is how
                          fast you respond to new jobs.
                          <Heading>Be the first to hear about new jobs</Heading>
                          Subscribe to the newsletter and increase your chances
                          of winning the bounty. */}
                          {/* <hr /> */}
                        </Content>
                        {!user && <SignIn header="Start Referring" next="/jobs" />}
                        {/* <SubscribeForm
                          {...props}
                          success={success}
                        /> */}
                      </Box>
                    </Columns.Column>
                  )}
                </Columns>
              );
            }}
          </Formik>
        </Container>
      </Section>
    </Fragment>
  );
};

export default HowMuchCanEarn;
